import { useEffect, useState } from 'react';

/** The nearest ancestor that scrolls vertically: the box whose content passes under a sticky area. */
function scrollParentOf(element: HTMLElement): HTMLElement | null {
    for (let node = element.parentElement; node; node = node.parentElement) {
        const { overflowY } = getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
    }
    return null;
}

/**
 * Whether content is scrolled up under a sticky area: its scroll box is scrolled and the area sits at its
 * sticky offset. Only a scrolling page does that, such as a report whose charts take the place of the rows; a
 * list whose rows scroll in their own box leaves the page still, so its sticky area casts no shadow.
 */
export function useScrolledUnder(): [(area: HTMLElement | null) => void, boolean] {
    const [area, setArea] = useState<HTMLElement | null>(null);
    const [scrolledUnder, setScrolledUnder] = useState(false);

    useEffect(() => {
        if (!area) return;
        const scrollBox = scrollParentOf(area);
        if (!scrollBox) return;
        let frame = 0;
        const measure = () => {
            frame = 0;
            const offset = parseFloat(getComputedStyle(area).top) || 0;
            const stuck = area.getBoundingClientRect().top - scrollBox.getBoundingClientRect().top <= offset + 0.5;
            setScrolledUnder(scrollBox.scrollTop > 0 && stuck);
        };
        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(measure);
        };
        measure();
        scrollBox.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            scrollBox.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(frame);
        };
    }, [area]);

    return [setArea, scrolledUnder];
}
